import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X, Calendar, Clock } from 'lucide-react';

const CalendarModal = ({ isOpen, onClose, videos }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredDay, setHoveredDay] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [animating, setAnimating] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Animate in
  useEffect(() => {
    if (isOpen) {
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Group videos by date key (YYYY-MM-DD)
  const videosByDate = useMemo(() => {
    const map = {};
    (videos || []).forEach((v) => {
      const rawDate = v.data_agendamento || v.data_publicacao;
      if (!rawDate) return;

      // Parse date string — handle various formats
      let dateKey = null;
      if (rawDate.includes('T') || rawDate.match(/^\d{4}-\d{2}-\d{2}/)) {
        dateKey = rawDate.substring(0, 10);
      } else if (rawDate.match(/^\d{2}\/\d{2}\/\d{4}/)) {
        const [day, month, year] = rawDate.split('/');
        dateKey = `${year}-${month}-${day}`;
      } else if (rawDate.match(/^\d{2}\/\d{2}/)) {
        // Short format DD/MM — assume current year
        const [day, month] = rawDate.split('/');
        const year = new Date().getFullYear();
        dateKey = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      if (dateKey) {
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(v);
      }
    });
    return map;
  }, [videos]);

  const navigateMonth = useCallback((direction) => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + direction);
      return d;
    });
  }, []);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Calendar grid computation
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    // Empty cells before month start
    for (let i = 0; i < firstDay; i++) {
      cells.push({ type: 'empty', key: `empty-${i}` });
    }
    // Day cells
    for (let d = 1; d <= daysInMonth; d++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayVideos = videosByDate[dateKey] || [];
      const today = new Date();
      const isToday =
        d === today.getDate() &&
        month === today.getMonth() &&
        year === today.getFullYear();

      cells.push({
        type: 'day',
        day: d,
        dateKey,
        videos: dayVideos,
        isToday,
        key: `day-${d}`,
      });
    }
    return cells;
  }, [currentDate, videosByDate]);

  const handleDayMouseEnter = useCallback((e, cell) => {
    if (cell.videos.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
    setHoveredDay(cell.dateKey);
  }, []);

  const handleDayMouseLeave = useCallback(() => {
    setHoveredDay(null);
  }, []);

  if (!isOpen) return null;

  const weekdays = t('dashboard.calendar_weekdays', { returnObjects: true }) || ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
  const months = t('dashboard.calendar_months', { returnObjects: true }) || ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const currentMonthName = months[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  // Count total videos this month
  const totalVideosThisMonth = calendarData.reduce((sum, cell) => {
    if (cell.type === 'day') return sum + cell.videos.length;
    return sum;
  }, 0);

  const hoveredDayVideos = hoveredDay ? (videosByDate[hoveredDay] || []) : [];

  return (
    <>
      <style>{`
        @keyframes calendarOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes calendarSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cal-day-cell:hover {
          background: var(--dp-bg-card-hover) !important;
        }
        .cal-day-cell-has-videos:hover {
          background: rgba(13, 174, 255, 0.08) !important;
          border-color: rgba(13, 174, 255, 0.25) !important;
        }
        .cal-nav-btn:hover {
          background: var(--dp-bg-card-hover) !important;
          color: var(--dp-blue-500) !important;
        }
        .cal-close-btn:hover {
          background: rgba(248, 113, 113, 0.12) !important;
          color: var(--dp-red-500) !important;
        }
        .cal-today-btn:hover {
          background: var(--dp-blue-500) !important;
          color: #fff !important;
        }
      `}</style>

      {/* Overlay */}
      <div
        className="dp-modal-overlay"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'calendarOverlayIn 0.25s ease',
        }}
      >
        {/* Modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'var(--dp-bg-glass, rgba(28, 28, 28, 0.92))',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid var(--dp-border-medium)',
            borderRadius: '16px',
            width: '520px',
            maxWidth: '94vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5)',
            animation: 'calendarSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            fontFamily: 'var(--dp-font)',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--dp-border-subtle)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Calendar size={20} style={{ color: 'var(--dp-blue-500)' }} />
              <h2
                style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: 'var(--dp-text-primary)',
                  margin: 0,
                  letterSpacing: '-0.3px',
                }}
              >
                {t('dashboard.calendar_title')}
              </h2>
            </div>
            <button
              className="cal-close-btn"
              onClick={onClose}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle)',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--dp-text-secondary)',
                transition: 'all 0.2s',
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Month navigation */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px 12px',
            }}
          >
            <button
              className="cal-nav-btn"
              onClick={() => navigateMonth(-1)}
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle)',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--dp-text-secondary)',
                transition: 'all 0.2s',
              }}
            >
              <ChevronLeft size={18} />
            </button>

            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--dp-text-primary)',
                  letterSpacing: '-0.3px',
                }}
              >
                {currentMonthName} {currentYear}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: 'var(--dp-text-tertiary)',
                  marginTop: '2px',
                }}
              >
                {totalVideosThisMonth > 0
                  ? t('dashboard.calendar_videos_on_day', { count: totalVideosThisMonth })
                  : t('dashboard.calendar_no_videos')}
              </div>
            </div>

            <button
              className="cal-nav-btn"
              onClick={() => navigateMonth(1)}
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '8px',
                border: '1px solid var(--dp-border-subtle)',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--dp-text-secondary)',
                transition: 'all 0.2s',
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Today button */}
          <div style={{ padding: '0 24px 12px', textAlign: 'center' }}>
            <button
              className="cal-today-btn"
              onClick={goToToday}
              style={{
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--dp-blue-500)',
                background: 'rgba(13, 174, 255, 0.08)',
                border: '1px solid rgba(13, 174, 255, 0.2)',
                borderRadius: '20px',
                padding: '4px 14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'var(--dp-font)',
              }}
            >
              {t('dashboard.calendar_today')}
            </button>
          </div>

          {/* Calendar grid */}
          <div style={{ padding: '0 24px 24px' }}>
            {/* Weekday headers */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '4px',
                marginBottom: '6px',
              }}
            >
              {weekdays.map((day, idx) => (
                <div
                  key={idx}
                  style={{
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'var(--dp-text-tertiary)',
                    padding: '6px 0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Day cells */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '4px',
              }}
            >
              {calendarData.map((cell) => {
                if (cell.type === 'empty') {
                  return <div key={cell.key} style={{ aspectRatio: '1' }} />;
                }

                const hasVideos = cell.videos.length > 0;
                const multipleVideos = cell.videos.length > 1;

                return (
                  <div
                    key={cell.key}
                    className={`cal-day-cell ${hasVideos ? 'cal-day-cell-has-videos' : ''}`}
                    onMouseEnter={(e) => handleDayMouseEnter(e, cell)}
                    onMouseLeave={handleDayMouseLeave}
                    style={{
                      aspectRatio: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                      cursor: hasVideos ? 'pointer' : 'default',
                      position: 'relative',
                      transition: 'all 0.15s ease',
                      border: cell.isToday
                        ? '2px solid var(--dp-blue-500)'
                        : '1px solid transparent',
                      background: hasVideos
                        ? 'rgba(13, 174, 255, 0.04)'
                        : 'transparent',
                    }}
                  >
                    {/* Day number */}
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: cell.isToday ? '700' : hasVideos ? '600' : '400',
                        color: cell.isToday
                          ? 'var(--dp-blue-500)'
                          : hasVideos
                          ? 'var(--dp-text-primary)'
                          : 'var(--dp-text-secondary)',
                        lineHeight: 1,
                      }}
                    >
                      {cell.day}
                    </span>

                    {/* Video dot indicator */}
                    {hasVideos && (
                      <div
                        style={{
                          marginTop: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: 'var(--dp-green-500)',
                          }}
                        />
                      </div>
                    )}

                    {/* Multi-video count badge */}
                    {multipleVideos && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: 'var(--dp-blue-500)',
                          color: '#fff',
                          fontSize: '9px',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >
                        {cell.videos.length}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tooltip */}
        {hoveredDay && hoveredDayVideos.length > 0 && (
          <div
            style={{
              position: 'fixed',
              left: tooltipPos.x,
              top: tooltipPos.y,
              transform: 'translate(-50%, -100%)',
              background: 'var(--dp-bg-elevated, #1a1a2e)',
              border: '1px solid var(--dp-border-medium)',
              borderRadius: '10px',
              padding: '12px 14px',
              zIndex: 10001,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
              maxWidth: '280px',
              minWidth: '200px',
              pointerEvents: 'none',
              animation: 'calendarOverlayIn 0.15s ease',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--dp-text-tertiary)',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {t('dashboard.calendar_videos_on_day', { count: hoveredDayVideos.length })}
            </div>
            {hoveredDayVideos.map((video, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '6px 0',
                  borderTop: idx > 0 ? '1px solid var(--dp-border-subtle)' : 'none',
                }}
              >
                <div
                  style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    background: 'var(--dp-green-500)',
                    flexShrink: 0,
                    marginTop: '6px',
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--dp-text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {video.titulo}
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      color: 'var(--dp-text-tertiary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '2px',
                    }}
                  >
                    {video.canal_nome && <span>{video.canal_nome}</span>}
                    {video.horario && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <Clock size={9} />
                        {video.horario}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CalendarModal;
